import axios from "axios";

const extractDocumentId = (payload: unknown): string | undefined => {
  const container =
    payload && typeof payload === "object" && "data" in payload ? payload.data : payload;
  const rows = Array.isArray(container) ? container : undefined;
  const firstRow: unknown = rows?.[0];
  if (firstRow && typeof firstRow === "object" && "documentId" in firstRow) {
    return typeof firstRow.documentId === "string" ? firstRow.documentId : undefined;
  }
  return undefined;
};

/**
 * Strapi v5 core REST routes (PUT/DELETE /api/<collection>/:id) key on the
 * string `documentId` — numeric ids now 404. Fetched entities carry BOTH
 * `id` (numeric, still our identity everywhere) and `documentId`.
 *
 * Prefer threading the documentId from an already-fetched entity; when only a
 * constructed numeric id is available, fall back to a single lookup.
 */
export const fetchDocumentId = async (
  collectionUrl: string,
  id: number | string,
): Promise<string> => {
  const response = await axios.get<unknown>(
    `${collectionUrl}?filters[id][$eq]=${id}&fields[0]=documentId`,
  );
  const documentId = extractDocumentId(response.data);
  if (!documentId) {
    throw new Error(`No documentId found for id ${id} on ${collectionUrl}`);
  }
  return documentId;
};

export const resolveDocumentId = async (
  collectionUrl: string,
  id: number | string,
  entity?: { documentId?: string } | null,
): Promise<string> => entity?.documentId ?? fetchDocumentId(collectionUrl, id);

/**
 * Strapi v5 PUT bodies must contain only schema attributes: unknown/immutable
 * keys (id, documentId, timestamps) trigger a 400. Use this when a call site
 * spreads a whole fetched entity into a core-route PUT body.
 */
const STRAPI_ENTITY_META_KEYS = [
  "id",
  "documentId",
  "createdAt",
  "updatedAt",
  "publishedAt",
  "created_at",
  "updated_at",
  "published_at",
];

export const omitStrapiEntityMetaKeys = (
  entity: object,
  extraKeys: string[] = [],
): Record<string, unknown> => {
  const body: Record<string, unknown> = { ...entity };
  for (const key of [...STRAPI_ENTITY_META_KEYS, ...extraKeys]) {
    delete body[key];
  }
  return body;
};
